import { Card, Fab, Grid, Stack, Typography, useTheme } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router';

import { BreadcrumbsLinkProps, CustomBreadcrumbs, enqueueSnackbar, FullPageSpinner, Spinner, useUserDetailsStore } from '@zeta-core';
import { ChevronLeftSvgIcon } from '@zeta-core/assets';
import { FinancialMisService, getErrorMessage } from '@zeta-services';
import { IEmptyRequestResponse, IGenericErrorResponse } from '@zeta-types';
import { RouterLinks } from '../../advisor-route-paths';
import DistributionsTable from './distributions-table';
import FundBreakdownTable from './fund-breakdown-table';
import FundAllocationGraph from './portfolio-pie-chart';
import TransactionsTable from './transactions-table';

export const PortfolioDetail = ({ isClient }: { isClient?: boolean }) => {
  const location = useLocation();
  const state = location?.state;
  const userStore = useUserDetailsStore((state) => state.user);

  const [advisorDetail, setAdvisorDetail] = useState();
  const [holdingsDetail, setHoldingsDetail] = useState();
  const [category, setCategory] = useState<string>('SECTOR');
  const theme = useTheme();

  const LINKS: BreadcrumbsLinkProps[] = [
    {
      name: 'Portfolio',
      icon: <ChevronLeftSvgIcon />,
      href: isClient ? RouterLinks.clientPortfolio : RouterLinks.managedClientPortfolio,
      state: state,
    },
    { name: state?.details?.investmentType || state?.investmentType },
  ];

  const clientID = isClient ? userStore?.accessibleEntityID : state?.details?.clientId;

  const getAdvisorDetail = useMutation({
    mutationFn: (payload: IEmptyRequestResponse) =>
      isClient ? FinancialMisService().getAdvisorDetail(payload) : FinancialMisService().getAdvisorDetailAdvisor(payload),
    onSuccess: (response) => {
      setAdvisorDetail(response?.data);
    },
    onError: (error: IGenericErrorResponse) => {
      const errorCode = error?.response?.data?.error?.errorCode || error?.response?.data?.error[0]?.errorCode;
      enqueueSnackbar(getErrorMessage(errorCode), { variant: 'error' });
    },
  });

  useEffect(() => {
    if (clientID) {
      getAdvisorDetail?.mutateAsync({ clientID });
    }
  }, [clientID]);

  const getHoldingsWeightage = useMutation({
    mutationFn: (payload: IEmptyRequestResponse) =>
      isClient ? FinancialMisService().getHoldingsWeightage(payload) : FinancialMisService().getHoldingsWeightageAdvisor(payload),
    onSuccess: (response) => {
      setHoldingsDetail(response?.data);
    },
    onError: (error: IGenericErrorResponse) => {
      const errorCode = error?.response?.data?.error?.errorCode || error?.response?.data?.error[0]?.errorCode;
      enqueueSnackbar(getErrorMessage(errorCode), { variant: 'error' });
    },
  });

  useEffect(() => {
    if (clientID) {
      getHoldingsWeightage?.mutateAsync({ clientID, accountId: state?.details?.accountId || state?.accountId, category });
    }
  }, [clientID, category]);

  const getFirstCharAfterSpace = (str) => {
    const parts = str?.split(' ');
    return parts?.[1]?.charAt(0) || '';
  };

  return (
    <Stack gap={2}>
      <CustomBreadcrumbs links={LINKS} />
      {getAdvisorDetail?.isPending ? (
        <FullPageSpinner />
      ) : (
        <>
          <Grid container spacing={2}>
            <Grid
              size={{
                lg: 3
              }}>
              <Stack sx={{ backgroundColor: theme?.palette?.primary?.darker, borderRadius: 2.2, pb: 0, pt: 0.5 }}>
                <Card sx={{ p: 2, minHeight: '102px' }}>
                  <Typography variant="subtitle2" color="text.secondary" mb={2}>
                    Account Holder
                  </Typography>
                  <Typography variant="subtitle1">
                    <Fab sx={{ height: '30px !important', width: '30px !important', minHeight: '30px', mr: 1, boxShadow: 'none' }} color="text.secondary">
                      {isClient ? userStore?.firstName?.[0] : state?.clientData?.retailClientName[0]}
                    </Fab>
                    {isClient ? `${userStore?.title} ${userStore?.firstName} ${userStore?.lastName}` : `${state?.clientData?.retailClientName}`}
                  </Typography>
                </Card>
              </Stack>
            </Grid>

            <Grid
              size={{
                lg: 3
              }}>
              <Stack sx={{ backgroundColor: theme?.palette?.primary?.darker, borderRadius: 2.2, pb: 0, pt: 0.5 }}>
                <Card sx={{ p: 2, minHeight: '102px' }}>
                  <Typography variant="subtitle2" color="text.secondary" mb={2}>
                    Account Number
                  </Typography>
                  <Typography variant="subtitle1">{state?.details?.accountNumber || state?.accountNumber}</Typography>
                </Card>
              </Stack>
            </Grid>

            <Grid
              size={{
                lg: 3
              }}>
              <Stack sx={{ backgroundColor: theme?.palette?.primary?.darker, borderRadius: 2.2, pb: 0, pt: 0.5 }}>
                <Card sx={{ p: 2, minHeight: '102px' }}>
                  <Typography variant="subtitle2" color="text.secondary" mb={2}>
                    Financial Advisor
                  </Typography>
                  <Typography variant="subtitle1">
                    <Fab sx={{ height: '30px !important', width: '30px !important', minHeight: '30px', mr: 1, boxShadow: 'none' }} color="text.secondary">
                      {getFirstCharAfterSpace(advisorDetail?.financialAdvisor)}
                    </Fab>
                    {advisorDetail?.financialAdvisor}
                  </Typography>
                </Card>
              </Stack>
            </Grid>

            <Grid
              size={{
                lg: 3
              }}>
              <Stack sx={{ backgroundColor: theme?.palette?.primary?.darker, borderRadius: 2.2, pb: 0, pt: 0.5 }}>
                <Card sx={{ p: 2, minHeight: '102px' }}>
                  <Typography variant="subtitle2" color="text.secondary" mb={2}>
                    Financial Advisor Code
                  </Typography>
                  <Typography variant="subtitle1">{advisorDetail?.financialAdvisorCode}</Typography>
                </Card>
              </Stack>
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid
              size={{
                lg: 4
              }}>
              <Stack sx={{ pl: 0.5, backgroundColor: theme?.palette?.primary?.main, borderRadius: 3 }}>
                <Stack
                  sx={{
                    p: 2,
                    border: `1px solid ${theme?.palette?.grey[300]}`,
                    borderLeft: 'none',
                    borderRadius: 2,
                    backgroundColor: theme.palette.grey[200],
                  }}
                >
                  {getHoldingsWeightage?.isPending ? (
                    <Stack minHeight="20vh" justifyContent="center" alignItems="center">
                      <Spinner />
                    </Stack>
                  ) : (
                    <>
                      <Typography variant="h6">{state?.details?.investmentType} Holdings</Typography>
                      <FundAllocationGraph data={holdingsDetail} setCategory={setCategory} selected={category} />
                    </>
                  )}
                </Stack>
              </Stack>
            </Grid>
            <Grid
              size={{
                lg: 8
              }}>
              <Stack gap={2}>
                <Card>
                  <Typography variant="subtitle2" sx={{ p: 2 }}>
                    Fund Breakdown
                  </Typography>
                  <FundBreakdownTable isClient={isClient} />
                </Card>

                <Card>
                  <Typography variant="subtitle2" sx={{ p: 2 }}>
                    Distributions
                  </Typography>
                  <DistributionsTable isClient={isClient} />
                </Card>

                <Card>
                  <Typography variant="subtitle2" sx={{ p: 2 }}>
                    Transactions
                  </Typography>
                  <TransactionsTable isClient={isClient} />
                </Card>
              </Stack>
            </Grid>
          </Grid>
        </>
      )}
    </Stack>
  );
};
